/**
 * @type {Object} Lookup table of available breakpoints.
 *
 * Keep in sync with SCSS variables!
 */
export const BREAKPOINTS = {
  // Keep in sync with SCSS variables!
  xxs: 0,
  xs: 375,
  sm: 768,
  md: 1152,
  lg: 1600,
  xl: 1920,
};

export const MAIN_BREAKPOINTS = {
  desktop: 'md',
  tablet: 'sm',
  phone: 'xs',
};

/**
 * @type {Number} Global resize debounce value.
 */
export const RESIZE_DEBOUNCE = 100;

/**
 * @type {Object} The default 'unknown error' notification message.
 */
export const NOTIFICATION_UNKNOWN_ERROR = {
  message: {
    type: 'error',
    message: '',
    translationKey: 'globalMessages.unknownApiError',
  },
};

/**
 * @type {Object} Holds the available booking status values.
 */
export const BOOKING_STATUS_MAP = {
  WAITING_LIST: 'WAITING_LIST',
  PENDING: 'REGISTRATION_PENDING',
  APPROVED: 'APPROVED',
  PASSED: 'PASSED',
};

/**
 * @type {Object} Holds the available course status values.
 */
export const COURSE_STATUS_MAP = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

/**
 * @type {Object} Holds the available eIFU status values.
 */
export const ELECTRONIC_INSTRUCTION_STATUS_MAP = {
  VALID: 'VALID',
  INVALID: 'INVALID',
  IMPROVED: 'IMPROVED',
};

/**
 * @type {RegExp} Validation regex for eIFU reference numbers.
 */
export const VALID_REFERENCE_NUMBER_REGEX = /^(([a-zA-Z]{2}\d{1,7})|(\d{6}[a-zA-Z]{2})|([bB]{1}\d{6})|(\d{6,14}))|(([a-zA-Z]+)\-([a-zA-Z]+)\-([0-9]+))$/;

/**
 * @type {Object} Holds the available URL query params.
 */
export const URL_PARAM_MAP = {
  refNumber: 'ref-number',
  documentId: 'document-id',
  eifuBrand: 'brandFilter',
  eifuProductSearch: 'brand',
  showEifuDetail: 'show-detail',
  continueCourseRegistration: 'continue-course-registration',
  upgradeCustomer: 'frm',
  referrerURL: 'referrer',
  cdcActivationCode: 'errorCode',
  cdcShowLoginWindow: 'login',
  idsProductDetailView: 'product',
  idsWorkflowView: 'workflow',
  idsWorkflowStep: 'workflowStep',
  screensaver: 'screensaver',
  idsBusinessCardScannerSend: 'send',
};

/**
 * @type {Object} Holds the available values for the frm (`upgradeCustomer`) URL parameter.
 */
export const UPGRADE_CUSTOMER_PARAM_VALUE_MAP = {
  standard: 'std',
  device: 'dvc',
  academy: 'academy',
};

/**
 * @type {Object} Holds a list of available cookie names.
 */
export const COOKIE_NAME_MAP = {
  welcomeModalShown: 'WelcomeMessageShown_{userUid}',
  highlightUserNavigationFeature: 'HighlightFeature-{id}',
};

/**
 * @type {Boolean} Informs if the current browser supports the IntersectionObserver service.
 */
export const SUPPORTS_INTERSECTION_OBSERVER = !!window.IntersectionObserver;

/**
 * @type {Boolean} Holds a flag if local and session storage are available.
 * They can lead to exceptions if cookies are disabled on the client.
 */
export const IS_STORAGE_AVAILABLE = (function isStorageAvailable() {
  const keyValue = '__storage_test__';

  try {
    const storage = window.localStorage;

    storage.setItem(keyValue, keyValue);
    storage.removeItem(keyValue);

    return true;
  } catch {
    return false;
  }
})();

export const LOCAL_STORAGE_MAP = {
  cdcShowProgressiveProfiling: 'CDC_show_progressive_profiling',
  courseDetailData: 'courseDetailData',
  ivCourseFilter: 'iv_courseFilter',
  ivProductFilter: 'iv_productFilter',
  ivBlogFilter: 'iv_blogFilter',
  ivGatekeeperConfirmed: 'iv_gatekeeperConfirmed',
};

export const GOOGLE_MAPS_THEME_GRAY = [
  // @see https://snazzymaps.com/style/15/subtle-grayscale
  {
    featureType: 'administrative',
    elementType: 'all',
    stylers: [{ saturation: '-100' }],
  },
  {
    featureType: 'administrative.province',
    elementType: 'all',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        saturation: -100,
      },
      {
        lightness: 65,
      },
      {
        visibility: 'on',
      },
    ],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'all',
    stylers: [
      {
        lightness: -10,
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        saturation: -100,
      },
      {
        lightness: '50',
      },
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'road',
    elementType: 'all',
    stylers: [
      {
        saturation: '-100',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'all',
    stylers: [
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'all',
    stylers: [
      {
        lightness: '30',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'all',
    stylers: [
      {
        lightness: '40',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        saturation: -100,
      },
      {
        visibility: 'simplified',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        hue: '#ffff00',
      },
      {
        lightness: -25,
      },
      {
        saturation: -97,
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'labels',
    stylers: [
      {
        lightness: -25,
      },
      {
        saturation: -100,
      },
    ],
  },
];

/**
 * @type {Object} A map af all custom event names.
 */
export const CUSTOM_EVENTS_MAP = {
  notificationShow: 'vueNotificationShow',
  touchStart: 'touchstart',
  formbuilderRendered: 'FormBuilder-Form-Rendered',
  formbuilderHideField: 'FormBuilder-Hide-Form-Field',
};

/**
 * @type {Object} A map af customer segments.
 */
export const CUSTOMER_SEGMENTS = {
  guest: 'guest', // Unregistered users
  basic: 'basic',
  professionalPending: 'professional_pending',
  professional: 'professional',
};

/**
 * @type {Object} A map for the cdc activation codes.
 */
export const CDC_ACTIVATION_STATUSES = {
  emailConfirmed: '0',
  linkExpired: '403002',
};

/**
 * @type {Object} A map for the cdc activation codes.
 */
export const CUSTOMER_GENDER_MAP = {
  female: 'female',
  male: 'male',
  other: 'Other',
};

/**
 * @type {Array} A list of valid color names for c-focus-pin.
 */
export const FOCUS_PIN_COLORS = ['blue', 'green'];

/**
 * @type {Array} Size variations for c-focus-pin.
 */
export const FOCUS_PIN_SIZES = ['small', 'medium', 'large'];

/**
 * @type {Array} A list of valid margin types for c-cms-button-group.
 */
export const CMS_BUTTON_GROUP_MARGINS = ['none', 'small', 'medium', 'large'];

/**
 * @type {Array} A list of valid horizontal alignments for c-cms-button-group.
 */
export const CMS_BUTTON_HORIZONTAL_ALIGNMENT = ['left', 'center', 'right'];

/**
 * TODO: Consider to read the theme files or directory to have a computed list of themes.
 *
 * @type {Array} A list of all themes.
 */
export const THEMES_MAP = {
  default: 'default',
  sagemax: 'sagemax',
  candulor: 'candulor',
  'private-label': 'private-label',
};

/**
 * @type {String} The class of the page header element.
 */
export const PAGE_HEADER_CLASS = 'c-header__content';

/**
 * The root url of the styleguide.
 *
 * @type {String}.
 */
export const STYLEGUIDE_ROOT_URL = '/:locale?/styleguide';

/**
 * @type {String} The default date format.
 */
export const DATE_FORMAT_DEFAULT = 'DD.MM.YYYY';

/**
 * @type {String} The default time format.
 */
export const TIME_FORMAT_DEFAULT = 'HH:mm';

export const CDC_START_SCREEN_MAP = {
  basic: 'gigya-register-screen-basic',
  register: 'gigya-register-screen-pro',
  upgradeToBasic: 'gigya-login-screen',
  upgradeToProfessional: 'gigya-screen-upgrade-to-pro',
  upgradeToProfessionalAndRegisterDevice: 'gigya-screen-upgrade-and-register-device',
  registerDevice: 'gigya-register-device-screen',
};

/**
 * Contains list of all Pinia stores.
 */
export enum Store {
  Blog = 'blog',
  Breadcrumb = 'breadcrumb',
  Certificates = 'certificates',
  Courses = 'courses',
  CourseFavourites = 'courseFavourites',
  Eifu = 'eifu',
  Events = 'events',
  Notification = 'notification',
  Product = 'product',
  Profile = 'profile',
  Search = 'search',
  Session = 'session',
  UserCourses = 'userCourses',
  Quiz = 'quiz'
}

/**
 * Holds all window storage keys used in the application.
 */
export enum WindowsStorage {
  Notifications = 'appNotifications',
}

/**
 * Contains list of API response messages.
 */
export enum ApiResponseMessageAction {
  Redirect = 'REDIRECT',
  PageReload = 'PAGE_RELOAD',
}
